/**
 * English strings of a domain's DNS zone (the DNS tab of the domain page): which name servers the
 * domain uses, the zone the system keeps for it, its records, versions and what was sent to the
 * DNS server. The Vietnamese file must have exactly the same keys. Every rule the DNS core names
 * (`subsystems/dns/core/src/validate.rs`, `RULES`) and every version reason has a sentence here
 * (`tests/closed-set-labels.test.mjs`).
 */
export const dns = {
  ns: {
    title: 'DNS zone',
    kind: {
      TINO: "Tino's default name servers",
      PARKED: 'Parked',
      EXTERNAL: 'External name servers',
      NONE: 'No name servers',
    },
    kindHint: {
      TINO: "The domain uses Tino's default name servers ({{list}}).",
      PARKED: 'The domain uses the parking name servers ({{list}}) until it is renewed.',
      EXTERNAL: 'The domain uses name servers outside Tino ({{list}}).',
      NONE: 'No name servers are recorded for the domain.',
    },
    kindUnknown: "Name servers: {{list}}. Their kind is unknown: Tino's default and parking name servers could not be read.",
  },
  noZone: {
    TINO: "The system does not keep a DNS zone for this domain yet. Its records are on Tino's name servers, outside the system: change them there.",
    PARKED: 'The parking name servers answer for the domain; the system keeps no zone for it.',
    EXTERNAL: 'The records are kept by whoever runs those name servers; the system keeps no zone for this domain.',
    NONE: 'The system keeps no zone for this domain.',
  },
  loadFailed: 'The DNS zone could not be read',
  zone: {
    title: 'DNS records',
    hint: 'The zone the system keeps for the domain. Each change asks for a reason, becomes a new version and is sent to the DNS server.',
    service: 'Zone',
    module: 'Module',
    state: 'Service state',
    dnssec: 'DNSSEC',
    nameservers: 'Name servers of the zone',
    notYet: 'Not known yet',
    secondary: 'A secondary zone: its records come from its primary servers.',
    notPointed: "The domain does not use the zone's name servers ({{list}}): the records changed here take no effect until it does.",
  },
  login: {
    title: 'Domain DNS login',
    hint: "A partner's customer manages this zone with the domain name and this password, without an account. Replacing or revoking it signs out every open session.",
    none: 'The zone has no DNS login.',
    state: 'DNS login',
    on: 'On',
    setBy: 'Set by',
    byStaff: 'Staff',
    byCustomer: 'The customer (account owner)',
    setAt: 'Set at',
    sessions: 'Open sessions',
    set: 'Set DNS password',
    replace: 'Replace DNS password',
    revoke: 'Revoke DNS login',
    setBody: 'The customer will sign in with the domain name and this password to manage the zone.',
    replaceBody: 'The old password stops working and every open session is signed out.',
    password: 'DNS password',
    passwordHint: '12 to 128 characters. A strong one is generated; copy it before saving: it is never shown again.',
    generate: 'Generate another',
    save: 'Save password',
    done: 'Password saved',
    doneHint: 'Give it to the customer through a safe channel; the system will not show it again.',
    revokeBody: 'Nobody can sign in with the DNS password any more, and every open session is signed out. The zone and its records stay.',
    revoked: 'The DNS login was revoked.',
  },
  dnssecState: {
    OFF: 'Off',
    ENABLING: 'Turning on',
    ON: 'On',
    DISABLING: 'Turning off',
  },
  status: {
    applied: 'Version {{version}} is on the DNS server.',
    waiting: 'Waiting to be sent to the DNS server: version {{desired}} (the server holds version {{applied}}).',
    waitingFirst: 'Waiting to be sent to the DNS server: version {{desired}} (nothing sent yet).',
    refused: 'The DNS server refused the last change: {{detail}}',
    failed: 'The last sending failed: {{detail}}',
    unknown: 'The last sending has no answer yet; it is being checked with the DNS server.',
  },
  col: {
    name: 'Name',
    type: 'Type',
    ttl: 'TTL',
    value: 'Value',
    source: 'Source',
  },
  source: {
    customer: 'Customer',
    email: 'E-mail',
    website: 'Website',
    cloud: 'Cloud server',
    hosting: 'Hosting',
    other: 'Service {{owner}}',
  },
  readOnly: 'Kept by {{source}}: it changes with that service.',
  empty: 'The zone has no records yet.',
  action: {
    add: 'Add record',
    edit: 'Edit',
    remove: 'Delete',
    export: 'Zone file',
    import: 'Import zone file',
    template: 'Apply template',
    rollback: 'Back to this version',
  },
  form: {
    name: 'Name',
    nameHint: '@ for the domain itself, or a name under it (www, mail, _dmarc).',
    type: 'Type',
    ttl: 'TTL (seconds)',
    values: 'Values',
    valuesHint: 'One record per line, e.g. "10 mail.example.vn." for MX. A name without a final dot is taken as written.',
    txtHint: 'Text without quotes is quoted for you (long text is split into 255-character parts).',
    others: 'Also at this name and type, kept by other services (they stay): {{list}}',
    addTitle: 'Add a record',
    editTitle: 'Edit {{name}} {{type}}',
    removeTitle: 'Delete {{name}} {{type}}',
    removeBody: "The customer's records of {{name}} {{type}} are deleted. Records of other services stay.",
    save: 'Save',
    removeSubmit: 'Delete',
    violations: 'The records break these rules:',
    done: 'Saved as version {{version}}.',
    unchanged: 'Nothing changed.',
  },
  exportForm: {
    title: 'Zone file',
    hint: 'The zone as an RFC 1035 master file, from the records the system keeps.',
    download: 'Download',
  },
  importForm: {
    title: 'Import a zone file',
    text: 'Zone file (RFC 1035)',
    mode: 'How',
    replace: "Replace all the customer's records",
    merge: 'Replace only the names and types in the file',
    skipped: 'Not kept ({{count}}):',
    warnings: 'Warnings:',
    done: 'Imported as version {{version}}.',
    skippedReason: {
      APEX_NS: "the zone's own name servers (kept by the DNS server)",
      DNSSEC: 'a DNSSEC record (made by the DNS server when signing)',
      UNSUPPORTED_TYPE: 'a record type the system does not keep',
    },
  },
  templateForm: {
    title: 'Apply a template',
    template: 'Template',
    mode: 'How',
    merge: 'Add to the records (sets of the same name and type are replaced)',
    replace: "Replace all the customer's records",
    done: 'Applied as version {{version}}.',
  },
  templates: {
    web: { name: 'Website (IPv4)', description: 'The domain at one address, www as an alias of the domain.' },
    'no-mail': { name: 'Domain that sends and receives no mail', description: 'Null MX (RFC 7505), an SPF record that allows no sender, DMARC reject.' },
    'caa-letsencrypt': { name: "Certificates from Let's Encrypt only", description: "Only Let's Encrypt may issue certificates; reports go to an e-mail address." },
  },
  vars: {
    ipv4: 'IPv4 address',
    report_email: 'E-mail for reports',
  },
  versions: {
    title: 'Versions',
    hint: 'Each change of the records is kept whole; going back makes a new version.',
    col: { version: 'Version', reason: 'Change', staffReason: 'Reason', note: 'Note', by: 'By', at: 'When' },
    system: 'System',
    current: 'current',
    toVersion: 'back to version {{version}}',
    rollbackTitle: 'Back to version {{version}}',
    rollbackBody: "The customer's records become those of version {{version}} again, as a new version. Records of other services stay as they are now.",
    done: 'Back to version {{from}} as version {{version}}.',
    rollbackRecords: "The customer's records in version {{version}}:",
    rollbackEmpty: "Version {{version}} has no record of the customer's: going back removes every record of the customer's.",
  },
  reason: {
    CREATE: 'Zone created',
    EDIT: 'Records edited',
    IMPORT: 'Imported from a zone file',
    TEMPLATE: 'Template applied',
    ROLLBACK: 'Back to an earlier version',
    PUBLISH: 'Records added by a service',
    WITHDRAW: 'Records removed with a service',
  },
  ops: {
    title: 'Sent to the DNS server',
    col: { op: 'Operation', status: 'Status', attempts: 'Attempts', at: 'Queued', detail: 'Detail' },
  },
  opName: {
    create: 'Create the zone',
    apply: 'Apply the records',
    repair: 'Repair the differences',
    dnssec_enable: 'Turn DNSSEC on',
    dnssec_disable: 'Turn DNSSEC off',
    allow_transfer: 'Allow zone transfers',
    native_template: "Apply the server's template",
    suspend: 'Suspend',
    unsuspend: 'Resume',
    terminate: 'Remove the zone',
    change_plan: 'Change plan',
    renew: 'Renew',
  },
  op: {
    QUEUED: 'Waiting',
    SENDING: 'Sending',
    RUNNING: 'Running at the server',
    SUCCEEDED: 'Done',
    REFUSED: 'Refused',
    FAILED: 'Failed',
    UNKNOWN: 'No answer yet',
    CANCELLED: 'Cancelled',
  },
  rule: {
    CAA_FLAGS: 'CAA flags are 0 or 128.',
    CAA_IODEF: 'iodef is a mailto: or https:// address.',
    CAA_TAG: 'A CAA tag is issue, issuewild or iodef.',
    CAA_VALUE: "issue names a certificate authority's domain, or ;.",
    CNAME_AT_APEX: 'The domain itself (@) cannot be a CNAME.',
    CNAME_EXCLUSIVE: 'A name with a CNAME has no other record.',
    CNAME_SINGLE: 'A name has at most one CNAME.',
    CNAME_TARGET: 'A CNAME points to a name.',
    DUPLICATE: 'The same record is listed twice.',
    DUPLICATE_SET: 'Two record sets have the same name and type.',
    EMPTY: 'A record set has at least one record.',
    MX_TARGET: 'A mail server is a host name.',
    NAME: 'The name is not valid in this zone.',
    NS_AT_APEX: "The zone's own name servers are kept by the DNS server, not here.",
    NS_TARGET: 'A name server is a host name.',
    NULL_MX: 'A null MX ("0 .") stands alone.',
    OCCLUDED: 'The name is below a delegated name (NS), so it would never be answered.',
    PTR_ZONE: 'PTR records belong in reverse zones (in-addr.arpa, ip6.arpa).',
    SRV_NAME: 'An SRV name is _service._protocol[.name].',
    SRV_NULL: '"No service" (target .) stands alone.',
    TLSA_DATA: 'The digest length does not match its matching type.',
    TLSA_FIELDS: 'Usage is 0–3, selector 0–1, matching type 0–2.',
    TLSA_NAME: 'A TLSA name is _port._protocol[.host].',
    TOO_MANY: 'Too many records in one set.',
    TTL: 'The TTL is outside the allowed bounds.',
    TXT_LENGTH: 'Each text string is at most 255 bytes.',
    TXT_TOTAL: 'The text record is too long.',
    TYPE_MISMATCH: 'A value of another type is in the set.',
    WILDCARD_NS: 'A wildcard name (*) cannot delegate.',
    ZONE_SIZE: 'The zone has too many record sets.',
  },
};
